/**
 * `History` — the recently-played ledger. `spec.md` §6.6 (`GET /api/history`),
 * `docs/design-direction.md` §8.2's ledger pattern (this is a ledger, not a card
 * grid) and §8.7–§8.10 for its states.
 *
 * This screen is deliberately the simplest of the ten: a `GET /api/history` page,
 * grouped into day sections (`Сегодня` / `Вчера` / `12 марта`), each row a `TrackRow`
 * — the same 56px ledger row the crate uses — with the row's relative-time fact
 * folded into its mono meta line rather than its own column, because T46's file list
 * gives this screen no CSS module of its own and `TrackRow` (already shipped, already
 * tested) owns the row's only two content slots: the meta line and the trailing
 * tabular figure. Reusing it verbatim is the whole point: a second, parallel ledger
 * row implementation is exactly the kind of drift `TrackRow`'s header warns about.
 *
 * **Relative time, never a raw timestamp** (the one hard requirement this screen
 * carries). {@link formatRelativeTime} is `now`-injected so it is deterministic under
 * test, and it degrades gracefully past 30 days to an absolute day heading rather than
 * inventing a "N weeks ago" that would drift from {@link formatDayHeading}'s own
 * vocabulary.
 *
 * **Day headers use the shared `DownbeatRule` primitive** — the one heading
 * decoration in the whole system (§6.10) — so a history section break looks exactly
 * like every other section break in the app, not like a bespoke sticky bar this
 * screen invented for itself.
 *
 * Tapping a row plays the *loaded history page* as the queue, starting at that row —
 * the same "this list becomes the set list" contract `MyCrate` and the crate itself
 * both honour (`PUT /api/queue` with the ordered ids, then one `select`).
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';

import type { HistoryItem } from '@bl/shared';

import type { ScreenProps } from '../app/router';
import { ErrorState } from '../app/states/ErrorState';
import { Loading, StateFrame } from '../app/states/Loading';
import statesStyles from '../app/states/states.module.css';
import type { Wiring } from '../app/wiring';
import { getWiring } from '../app/wiring';
import { DownbeatRule } from '../components/primitives/DownbeatRule';
import { Halftone } from '../components/primitives/Halftone';
import { cx } from '../components/primitives/Rule';
import { TrackRow } from '../components/TrackRow';
import type { ApiClient, ApiFailure, HistoryArgs } from '../lib/api';
import { api, toApiFailure } from '../lib/api';
import { usePlayerStore } from '../store/player';
import { useQueueStore } from '../store/queue';

/* ══ copy ══════════════════════════════════════════════════════════════════════════ */

/** Sentence case: a Russian UI heading, not tracked mono caps (§4.3, §12.16). */
export const HISTORY_TITLE_RU = 'История';
export const HISTORY_SUBTITLE_RU = 'Недавно прослушанное';
export const HISTORY_EMPTY_RU = 'Здесь пока пусто';
export const HISTORY_LOAD_MORE_RU = 'Показать ещё';
export const HISTORY_GRID = 'ledger';

const HISTORY_PAGE_LIMIT = 50;

/* ══ day headings ══════════════════════════════════════════════════════════════════ */

/** Genitive month forms — the grammatical case a Russian "12 марта" needs. */
const MONTHS_GENITIVE_RU = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
] as const;

/** Midnight, in the viewer's own timezone, of the day containing `ms`. */
export function startOfLocalDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** `Сегодня` / `Вчера` / `12 марта` / `12 марта 2023` (a past year is only ever added). */
export function formatDayHeading(atSeconds: number, nowMs: number): string {
  const dayStart = startOfLocalDay(atSeconds * 1000);
  const todayStart = startOfLocalDay(nowMs);
  const diffDays = Math.round((todayStart - dayStart) / 86_400_000);
  if (diffDays === 0) return 'Сегодня';
  if (diffDays === 1) return 'Вчера';

  const d = new Date(atSeconds * 1000);
  const month = MONTHS_GENITIVE_RU[d.getMonth()] ?? '';
  const base = `${String(d.getDate())} ${month}`;
  const year = d.getFullYear();
  return year === new Date(nowMs).getFullYear() ? base : `${base} ${String(year)}`;
}

/**
 * `2 ч назад` — the mono relative-time fact §T46 asks for, never a raw timestamp.
 *
 * Degrades in four honest steps (just now / minutes / hours / days) and, past 30
 * days, falls back to {@link formatDayHeading}'s own absolute vocabulary rather than
 * inventing a "N weeks ago" the day headings do not otherwise use.
 */
export function formatRelativeTime(atSeconds: number, nowMs: number): string {
  const deltaS = Math.max(0, Math.floor(nowMs / 1000) - atSeconds);
  if (deltaS < 45) return 'только что';
  if (deltaS < 3600) return `${String(Math.max(1, Math.round(deltaS / 60)))} мин назад`;
  if (deltaS < 86_400) return `${String(Math.round(deltaS / 3600))} ч назад`;
  if (deltaS < 86_400 * 30) return `${String(Math.round(deltaS / 86_400))} дн назад`;
  return formatDayHeading(atSeconds, nowMs);
}

/* ══ grouping ══════════════════════════════════════════════════════════════════════ */

export interface HistoryRowEntry {
  readonly item: HistoryItem;
  /** Absolute position in the whole loaded ledger — drives `TrackRow`'s bar line. */
  readonly index: number;
}

export interface HistoryDaySection {
  readonly key: string;
  readonly heading: string;
  readonly rows: readonly HistoryRowEntry[];
}

/**
 * Group an already-newest-first page into day sections, without re-sorting: the
 * server's `ORDER BY last_at DESC` (§6.6) is the truth, and grouping must not disturb
 * it even if two rows share a `lastPlayedAt` second.
 */
export function groupHistoryByDay(
  items: readonly HistoryItem[],
  nowMs: number,
): readonly HistoryDaySection[] {
  const sections: HistoryDaySection[] = [];
  const byKey = new Map<string, HistoryDaySection>();

  items.forEach((item, index) => {
    const key = String(startOfLocalDay(item.lastPlayedAt * 1000));
    let section = byKey.get(key);
    if (section === undefined) {
      section = { key, heading: formatDayHeading(item.lastPlayedAt, nowMs), rows: [] };
      byKey.set(key, section);
      sections.push(section);
    }
    (section.rows as HistoryRowEntry[]).push({ item, index });
  });

  return sections;
}

/* ══ the screen ════════════════════════════════════════════════════════════════════ */

export interface HistoryDeps {
  readonly client?: ApiClient;
  readonly queue?: typeof useQueueStore;
  readonly player?: typeof usePlayerStore;
  readonly wiring?: Pick<Wiring, 'select'>;
  /** Epoch milliseconds. Injected so day headings and relative time are testable. */
  readonly now?: () => number;
}

export type HistoryProps = ScreenProps & { readonly deps?: HistoryDeps };

interface HistoryPageState {
  readonly status: 'loading' | 'ready' | 'error';
  readonly items: readonly HistoryItem[];
  readonly nextCursor: string | null;
  readonly error: ApiFailure | null;
  readonly loadingMore: boolean;
}

const INITIAL_PAGE: HistoryPageState = {
  status: 'loading',
  items: [],
  nextCursor: null,
  error: null,
  loadingMore: false,
};

export function History({ deps = {} }: HistoryProps): JSX.Element {
  const client = deps.client ?? api;
  const queue = deps.queue ?? useQueueStore;
  const player = deps.player ?? usePlayerStore;
  const now = deps.now ?? Date.now;

  const [page, setPage] = useState<HistoryPageState>(INITIAL_PAGE);
  const currentTrackId = player((s) => s.trackId);
  const isPlaying = player((s) => s.isPlaying);

  const fetchPage = useCallback(
    async (args: HistoryArgs, append: boolean): Promise<void> => {
      setPage((prev) =>
        append
          ? { ...prev, loadingMore: true }
          : { status: 'loading', items: [], nextCursor: null, error: null, loadingMore: false },
      );
      try {
        const response = await client.history(args);
        setPage((prev) => ({
          status: 'ready',
          items: append ? [...prev.items, ...response.items] : response.items,
          nextCursor: response.nextCursor,
          error: null,
          loadingMore: false,
        }));
      } catch (error) {
        const failure = toApiFailure(error);
        setPage((prev) =>
          append
            ? { ...prev, loadingMore: false, error: failure }
            : { status: 'error', items: [], nextCursor: null, error: failure, loadingMore: false },
        );
      }
    },
    [client],
  );

  useEffect(() => {
    void fetchPage({ limit: HISTORY_PAGE_LIMIT }, false);
  }, [fetchPage]);

  const retry = useCallback((): void => {
    void fetchPage({ limit: HISTORY_PAGE_LIMIT }, false);
  }, [fetchPage]);

  const loadMore = useCallback((): void => {
    if (page.nextCursor === null) return;
    void fetchPage({ limit: HISTORY_PAGE_LIMIT, cursor: page.nextCursor }, true);
  }, [fetchPage, page.nextCursor]);

  const play = useCallback(
    async (index: number): Promise<void> => {
      await queue.getState().replace(page.items, index);
      const w = deps.wiring ?? getWiring();
      await w.select(index);
    },
    [deps.wiring, page.items, queue],
  );

  const nowMs = now();
  const sections = useMemo(() => groupHistoryByDay(page.items, nowMs), [page.items, nowMs]);

  let body: JSX.Element;
  if (page.status === 'error') {
    body = <ErrorState failure={page.error as ApiFailure} grid={HISTORY_GRID} onRetry={retry} />;
  } else if (page.status === 'loading') {
    body = <Loading grid={HISTORY_GRID} rows={5} />;
  } else if (page.items.length === 0) {
    body = (
      <StateFrame grid={HISTORY_GRID} state="empty" role="group" ariaLabel={HISTORY_EMPTY_RU}>
        <p
          className={cx('t-mono', 't-13', statesStyles.cyr, statesStyles.string)}
          data-state-string="history-empty"
        >
          {HISTORY_EMPTY_RU}
        </p>
      </StateFrame>
    );
  } else {
    body = (
      <StateFrame grid={HISTORY_GRID} state="content" role="group" ariaLabel={HISTORY_TITLE_RU}>
        {sections.map((section) => (
          <Fragment key={section.key}>
            <DownbeatRule label={section.heading} />
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {section.rows.map(({ item, index }) => (
                <TrackRow
                  key={item.id}
                  as="li"
                  title={item.title}
                  performer={item.performer}
                  durationS={item.durationS}
                  messageId={item.id}
                  seed={item.plate.seed}
                  meta={[formatRelativeTime(item.lastPlayedAt, nowMs)]}
                  current={currentTrackId === item.id}
                  playing={isPlaying}
                  index={index}
                  onPlay={() => {
                    void play(index);
                  }}
                />
              ))}
            </ul>
          </Fragment>
        ))}

        {page.nextCursor === null ? null : (
          <button
            type="button"
            className="t-ui t-17"
            data-action="load-more"
            disabled={page.loadingMore}
            onClick={loadMore}
          >
            {HISTORY_LOAD_MORE_RU}
          </button>
        )}
      </StateFrame>
    );
  }

  return (
    <div>
      <Halftone as="header">
        <h1 className="t-ui t-28" style={{ fontWeight: 700 }}>
          {HISTORY_TITLE_RU}
        </h1>
        <p className="t-ui t-15">{HISTORY_SUBTITLE_RU}</p>
      </Halftone>
      {body}
    </div>
  );
}
